<template>
    <div class="automation-insights" v-animate-css="animationObject">
        <div class="insights-container">
            <div class="header-container">
                <div class="title">İstatistikler</div>
                <div class="filter-container">
                    <div class="filter-item">
                        <a-tooltip title="Tarih Aralığı">
                            <i class="material-icons filter-icon">date_range</i>
                        </a-tooltip>
                        <a-range-picker
                            v-model="filters.dateRange"
                            :format="'DD/MM/YYYY'"
                            :placeholder="['Başlangıç', 'Bitiş']"
                            style="width: 220px"
                            :ranges="{
                                'Son Hafta': [moment().subtract(7, 'days'), moment()],
                                'Son Ay': [moment().subtract(30, 'days'), moment()],
                                'Son 3 Ay': [moment().subtract(90, 'days'), moment()]
                            }"
                            @change="handleDateChange"
                        />
                    </div>

                    <div class="filter-item">
                        <a-tooltip title="Durum Filtresi">
                            <i class="material-icons filter-icon">filter_list</i>
                        </a-tooltip>
                        <a-select
                            v-model="filters.status"
                            :mode="'multiple'"
                            :placeholder="'Durum Filtresi'"
                            style="width: 220px"
                            @change="handleStatusChange"
                        >
                            <a-select-option value="scheduled">
                                <div class="status-option">
                                    <i class="material-icons status-icon scheduled">schedule</i>
                                    <span>Planlanmış</span>
                                </div>
                            </a-select-option>
                            <a-select-option value="running">
                                <div class="status-option">
                                    <i class="material-icons status-icon running">play_arrow</i>
                                    <span>Çalışıyor</span>
                                </div>
                            </a-select-option>
                            <a-select-option value="waiting-reply">
                                <div class="status-option">
                                    <i class="material-icons status-icon waiting">hourglass_empty</i>
                                    <span>Yanıt Bekliyor</span>
                                </div>
                            </a-select-option>
                            <a-select-option value="done">
                                <div class="status-option">
                                    <i class="material-icons status-icon done">check_circle</i>
                                    <span>Tamamlandı</span>
                                </div>
                            </a-select-option>
                            <a-select-option value="failed">
                                <div class="status-option">
                                    <i class="material-icons status-icon failed">error</i>
                                    <span>Başarısız</span>
                                </div>
                            </a-select-option>
                            <a-select-option value="cancelled">
                                <div class="status-option">
                                    <i class="material-icons status-icon cancelled">cancel</i>
                                    <span>İptal Edildi</span>
                                </div>
                            </a-select-option>
                        </a-select>
                    </div>

                    <a-tooltip title="Filtreleri Temizle" v-if="hasActiveFilters()">
                        <SupsisButton icon="clear_all" @click="clearAllFilters" />
                    </a-tooltip>

                    <a-tooltip title="Verileri Yenile">
                        <SupsisButton primary icon="refresh" @click="refreshData" />
                    </a-tooltip>
                </div>
            </div>

            <div class="active-filters-bar" v-if="hasActiveFilters()">
                <div class="active-filter-info">
                    <i class="material-icons">filter_alt</i>
                    <span>Aktif Filtreler:</span>
                </div>
                <div class="active-filters-tags">
                    <a-tag v-if="hasDateFilter()" closable @close="clearDateFilter" color="green">
                        <i class="material-icons">date_range</i>
                        <span>{{ formatDateRange() }}</span>
                    </a-tag>
                    
                    <a-tag v-for="status in filters.status" :key="status" closable @close="() => removeStatus(status)" :color="getStatusColor(status)">
                        <i class="material-icons">{{ getStatusIcon(status) }}</i>
                        <span>{{ getStatusName(status) }}</span>
                    </a-tag>
                </div>
            </div>

            <a-spin :spinning="loading">
                <!-- Genel İstatistikler -->
                <div class="stats-cards">
                    <a-card class="stats-card">
                        <template #title>
                            <div class="card-title">
                                <i class="material-icons">task_alt</i>
                                Toplam Görevler
                            </div>
                        </template>
                        <div class="card-content">
                            <div class="number">{{ formatNumber(insightData.stats.totalTasks) }}</div>
                            <div class="description">Yürütülen Görevler</div>
                        </div>
                    </a-card>

                    <a-card class="stats-card">
                        <template #title>
                            <div class="card-title">
                                <i class="material-icons">horizontal_rule</i>
                                Başarı Oranı
                            </div>
                        </template>
                        <div class="card-content">
                            <div class="number">{{ formatPercentage(insightData.stats.successRate) }}%</div>
                            <div class="description">Başarılı Görevler</div>
                        </div>
                    </a-card>

                    <a-card class="stats-card">
                        <template #title>
                            <div class="card-title">
                                <i class="material-icons">timer</i>
                                Ortalama Süre
                            </div>
                        </template>
                        <div class="card-content">
                            <div class="number">{{ formatDuration(insightData.stats.avgDuration) }}</div>
                            <div class="description">Ortalama Çalışma Süresi</div>
                        </div>
                    </a-card>

                    <a-card class="stats-card">
                        <template #title>
                            <div class="card-title">
                                <i class="material-icons">pending</i>
                                Aktif Görevler
                            </div>
                        </template>
                        <div class="card-content">
                            <div class="number">{{ formatNumber(insightData.stats.totalRunningTasks) }}</div>
                            <div class="description">Şu Anda Çalışıyor</div>
                        </div>
                    </a-card>
                </div>

                <!-- Grafikler -->
                <div class="chart-container">
                    <a-row :gutter="16">
                        <a-col :span="12">
                            <a-card class="chart-card">
                                <template #title>
                                    <div class="card-title">
                                        <i class="material-icons">timeline</i>
                                        Günlük Görev Çalıştırma
                                    </div>
                                </template>
                                <div class="chart">
                                    <v-chart :options="dailyTasksOptions" autoresize />
                                </div>
                            </a-card>
                        </a-col>
                        <a-col :span="12">
                            <a-card class="chart-card">
                                <template #title>
                                    <div class="card-title">
                                        <i class="material-icons">pie_chart</i>
                                        Durum Dağılımı
                                    </div>
                                </template>
                                <div class="chart">
                                    <v-chart :options="statusDistributionOptions" autoresize />
                                </div>
                            </a-card>
                        </a-col>
                    </a-row>
                </div>

                <div class="chart-container">
                    <a-row :gutter="16">
                        <a-col :span="12">
                            <a-card class="chart-card">
                                <template #title>
                                    <div class="card-title">
                                        <i class="material-icons">timer</i>
                                        Süre Dağılımı
                                    </div>
                                </template>
                                <div class="chart">
                                    <v-chart :options="durationDistributionOptions" autoresize />
                                </div>
                            </a-card>
                        </a-col>
                        <a-col :span="12">
                            <a-card class="chart-card">
                                <template #title>
                                    <div class="card-title">
                                        <i class="material-icons">error_outline</i>
                                        Sonlandırma Nedenleri
                                    </div>
                                </template>
                                <div class="chart">
                                    <v-chart :options="terminateReasonsOptions" autoresize />
                                </div>
                            </a-card>
                        </a-col>
                    </a-row>
                </div>

                <!-- Tablo -->
                <a-card class="data-table-card">
                    <template #title>
                        <div class="card-title">
                            <i class="material-icons">leaderboard</i>
                            Otomasyon Performansı
                        </div>
                    </template>
                    <a-table
                        :columns="automationPerformanceColumns"
                        :data-source="automationPerformanceData"
                        :pagination="{ pageSize: 5 }"
                        :rowKey="record => record.automationId"
                    >
                        <template #action="{ record }">
                            <a-button type="link" @click="viewAutomationDetails(record.automationId)">
                                Görüntüle
                            </a-button>
                        </template>
                    </a-table>
                </a-card>
            </a-spin>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import { AUTOMATION_API } from "../../../../../../api/automation";
import log2 from "../../../../../../utils/log2";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";

import VChart from "vue-echarts";
import "echarts/lib/chart/bar";
import "echarts/lib/chart/line";
import "echarts/lib/chart/pie";
import "echarts/lib/component/tooltip";
import "echarts/lib/component/legend";
import "echarts/lib/component/title";

const log = log2("AutomationInsights");

export default {
    name: "AutomationInsights",
    components: {
        VChart,
        SupsisButton
    },
    props: {
        automation: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            loading: false,
            animationObject: { classes: "fadeInDown", delay: 0, duration: 300 },
            filters: {
                dateRange: [moment().subtract(30, "days"), moment()],
                status: []
            },
            statusInfo: {
                scheduled: { name: "Planlanmış", icon: "schedule", color: "blue" },
                running: { name: "Çalışıyor", icon: "play_arrow", color: "orange" },
                "waiting-reply": { name: "Yanıt Bekliyor", icon: "hourglass_empty", color: "purple" },
                done: { name: "Tamamlandı", icon: "check_circle", color: "green" },
                failed: { name: "Başarısız", icon: "error", color: "red" },
                cancelled: { name: "İptal Edildi", icon: "cancel", color: "gray" }
            },
            insightData: {
                timeRange: {
                    startDate: null,
                    endDate: null
                },
                dailyTasks: [],
                statusDistribution: [],
                terminateReasons: [],
                stats: {
                    totalTasks: 0,
                    avgDuration: 0,
                    successRate: 0,
                    totalSuccessfulTasks: 0,
                    totalFailedTasks: 0,
                    totalRunningTasks: 0,
                    totalWaitingTasks: 0
                }
            },
            durationStatistics: {
                durationRanges: [],
                automationAvgDuration: [],
                stats: {
                    totalTasksWithDuration: 0,
                    avgDuration: 0,
                    minDuration: 0,
                    maxDuration: 0,
                    totalDuration: 0,
                    avgDurationInSeconds: 0
                }
            },
            statusData: {
                statusDistribution: [],
                dailyPlans: [],
                stats: {
                    totalPlans: 0,
                    scheduledCount: 0,
                    completedCount: 0,
                    cancelledCount: 0,
                    completionRate: 0
                }
            },
            terminateData: {
                terminateReasonsDistribution: [],
                weeklyTerminateReasons: [],
                automationTerminateReasons: []
            },
            automationPerformanceColumns: [
                {
                    title: "Otomasyon Adı",
                    dataIndex: "automationName",
                    key: "automationName"
                },
                {
                    title: "Ortalama Süre",
                    dataIndex: "avgDuration",
                    key: "avgDuration",
                    customRender: text => this.formatDuration(text)
                },
                {
                    title: "Çalıştırma Sayısı",
                    dataIndex: "count",
                    key: "count",
                    sorter: (a, b) => a.count - b.count
                },
                {
                    title: "İşlemler",
                    key: "action",
                    width: "15%",
                    scopedSlots: { customRender: "action" }
                }
            ]
        };
    },
    computed: {
        moment() {
            return moment;
        },
        dailyTasksOptions() {
            return {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow'
                    }
                },
                legend: {
                    data: [
                        "Toplam Görevler",
                        "Başarılı",
                        "Başarısız"
                    ]
                },
                xAxis: {
                    type: 'category',
                    data: this.insightData.dailyTasks.map(item => moment(item.date).format('DD/MM'))
                },
                yAxis: {
                    type: 'value'
                },
                series: [
                    {
                        name: "Toplam Görevler",
                        type: 'line',
                        data: this.insightData.dailyTasks.map(item => item.count),
                        lineStyle: {
                            color: '#1890ff'
                        }
                    },
                    {
                        name: "Başarılı",
                        type: 'bar',
                        stack: 'tasks',
                        data: this.insightData.dailyTasks.map(item => item.successCount),
                        itemStyle: {
                            color: '#52c41a'
                        }
                    },
                    {
                        name: "Başarısız",
                        type: 'bar',
                        stack: 'tasks',
                        data: this.insightData.dailyTasks.map(item => item.failedCount),
                        itemStyle: {
                            color: '#f5222d'
                        }
                    }
                ]
            };
        },
        statusDistributionOptions() {
            const statusColors = {
                'scheduled': '#1890ff',
                'running': '#fa8c16',
                'waiting-reply': '#722ed1',
                'done': '#52c41a',
                'failed': '#f5222d',
                'cancelled': '#bfbfbf'
            };

            const statusNames = {
                'scheduled': "Planlanmış",
                'running': "Çalışıyor",
                'waiting-reply': "Yanıt Bekliyor",
                'done': "Tamamlandı",
                'failed': "Başarısız",
                'cancelled': "İptal Edildi"
            };

            return {
                tooltip: {
                    trigger: 'item',
                    formatter: '{b}: {c} ({d}%)'
                },
                legend: {
                    orient: 'vertical',
                    right: 10,
                    top: 'center',
                    data: this.insightData.statusDistribution.map(item => statusNames[item.status])
                },
                series: [
                    {
                        type: 'pie',
                        radius: ['50%', '70%'],
                        avoidLabelOverlap: false,
                        label: {
                            show: false
                        },
                        emphasis: {
                            label: {
                                show: true,
                                fontSize: '18',
                                fontWeight: 'bold'
                            }
                        },
                        labelLine: {
                            show: false
                        },
                        data: this.insightData.statusDistribution.map(item => ({
                            name: statusNames[item.status],
                            value: item.count,
                            itemStyle: {
                                color: statusColors[item.status]
                            }
                        }))
                    }
                ]
            };
        },
        durationDistributionOptions() {
            return {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow'
                    }
                },
                xAxis: {
                    type: 'category',
                    data: this.durationStatistics.durationRanges.map(item => item.range),
                    axisLabel: {
                        interval: 0,
                        rotate: 30
                    }
                },
                yAxis: {
                    type: 'value'
                },
                series: [
                    {
                        name: "Görev Sayısı",
                        type: 'bar',
                        data: this.durationStatistics.durationRanges.map(item => item.count),
                        itemStyle: {
                            color: '#1890ff'
                        }
                    }
                ]
            };
        },
        terminateReasonsOptions() {
            return {
                tooltip: {
                    trigger: 'item',
                    formatter: '{b}: {c} ({d}%)'
                },
                legend: {
                    type: 'scroll',
                    orient: 'vertical',
                    right: 10,
                    top: 'center',
                    data: this.terminateData.terminateReasonsDistribution.map(item => item.reason)
                },
                series: [
                    {
                        type: 'pie',
                        radius: '55%',
                        center: ['40%', '50%'],
                        data: this.terminateData.terminateReasonsDistribution.map(item => ({
                            name: item.reason,
                            value: item.count
                        }))
                    }
                ]
            };
        },
        automationPerformanceData() {
            return this.durationStatistics.automationAvgDuration;
        }
    },
    methods: {
        ...mapActions({}),
        hasActiveFilters() {
            return this.hasDateFilter() || this.filters.status.length > 0;
        },
        hasDateFilter() {
            if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return false;
            
            const defaultStart = moment().subtract(30, "days").startOf('day');
            const defaultEnd = moment().endOf('day');
            
            const start = moment(this.filters.dateRange[0]).startOf('day');
            const end = moment(this.filters.dateRange[1]).endOf('day');
            
            return !start.isSame(defaultStart, 'day') || !end.isSame(defaultEnd, 'day');
        },
        formatDateRange() {
            if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return '';
            
            return `${moment(this.filters.dateRange[0]).format('DD.MM.YYYY')} - ${moment(this.filters.dateRange[1]).format('DD.MM.YYYY')}`;
        },
        getStatusName(status) {
            return this.statusInfo[status]?.name || status;
        },
        getStatusIcon(status) {
            return this.statusInfo[status]?.icon || "help_outline";
        },
        getStatusColor(status) {
            return this.statusInfo[status]?.color || "default";
        },
        clearDateFilter() {
            this.filters.dateRange = [moment().subtract(30, "days"), moment()];
            this.fetchData();
        },
        clearStatusFilter() {
            this.filters.status = [];
            this.fetchData();
        },
        clearAllFilters() {
            this.filters.dateRange = [moment().subtract(30, "days"), moment()];
            this.filters.status = [];
            this.fetchData();
        },
        removeStatus(status) {
            this.filters.status = this.filters.status.filter(s => s !== status);
            this.fetchData();
        },
        async fetchData() {
            this.loading = true;
            try {
                const filters = {
                    automationId: this.automation._id,
                    status: this.filters.status
                };
                const timeRange = {
                    startDate: this.filters.dateRange[0].toISOString(),
                    endDate: this.filters.dateRange[1].toISOString()
                };

                // Tüm veri çağrılarını paralel yapalım
                const [insightData, durationStatistics, terminateData] = await Promise.all([
                    AUTOMATION_API.INSIGHTS.getAutomationInsights(filters, timeRange),
                    AUTOMATION_API.INSIGHTS.getDurationStatistics(filters),
                    AUTOMATION_API.INSIGHTS.getTerminateReasons(filters)
                ]);

                this.insightData = insightData;
                this.durationStatistics = durationStatistics;
                this.terminateData = terminateData;
            } catch (error) {
                log.error("İstatistikler alınırken hata oluştu:", error);
                this.$message.error("İstatistikleri alırken bir hata oluştu");
            } finally {
                this.loading = false;
            }
        },
        handleDateChange(date) {
            if (date && date.length === 2) {
                this.filters.dateRange = date;
                this.fetchData();
            }
        },
        handleStatusChange(values) {
            this.filters.status = values;
            this.fetchData();
        },
        refreshData() {
            this.fetchData();
        },
        formatNumber(value) {
            return value ? value.toLocaleString() : 0;
        },
        formatPercentage(value) {
            return value ? value.toFixed(2) : 0;
        },
        formatDuration(milliseconds) {
            if (!milliseconds) return "0 ms";
            
            const seconds = milliseconds / 1000;
            
            if (seconds < 1) {
                return `${milliseconds.toFixed(0)} ms`;
            } else if (seconds < 60) {
                return `${seconds.toFixed(2)} sn`;
            } else if (seconds < 3600) {
                const minutes = Math.floor(seconds / 60);
                const remainingSeconds = seconds % 60;
                return `${minutes} dk ${remainingSeconds.toFixed(0)} sn`;
            } else {
                const hours = Math.floor(seconds / 3600);
                const minutes = Math.floor((seconds % 3600) / 60);
                return `${hours} sa ${minutes} dk`;
            }
        },
        viewAutomationDetails(automationId) {
            // Burada belirli bir otomasyonun detaylarını görüntülemek için 
            // yönlendirme veya modal açma işlemi yapılabilir
            if (automationId === this.automation._id) {
                this.$message.info("Zaten bu otomasyonu görüntülüyorsunuz");
            } else {
                this.$router.push(`/automations/${automationId}`);
            }
        }
    },
    mounted() {
        this.fetchData();
    }
};
</script>

<style lang="scss" scoped>
.automation-insights {
    box-shadow: 0 4px 20px #00000040;
    background: #ffffff;
    padding: 25px;
    border-radius: 6px;
    height: calc(100vh - 100px);
    overflow: auto;
    width: 100%;

    .insights-container {
        display: flex;
        flex-direction: column;
        gap: 20px;

        .header-container {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 15px;
            margin-bottom: 8px;

            .title {
                font-size: 24px;
                font-weight: 700;
                color: var(--color-text-default);
            }

            .filter-container {
                display: flex;
                gap: 10px;
                flex-wrap: wrap;
                align-items: center;
                
                .filter-item {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    
                    .filter-icon {
                        color: var(--color-primary);
                        font-size: 20px;
                    }
                }
            }
        }
        
        .active-filters-bar {
            display: flex;
            align-items: center;
            gap: 12px;
            background-color: var(--color-bg-neutral-default);
            padding: 8px 12px;
            border-radius: 6px;
            margin-bottom: 20px;
            
            .active-filter-info {
                display: flex;
                align-items: center;
                gap: 6px;
                color: var(--color-text-secondary);
                
                i {
                    font-size: 18px;
                }
                
                span {
                    font-weight: 600;
                }
            }
            
            .active-filters-tags {
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                
                .ant-tag {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    margin-right: 0;
                    
                    i {
                        font-size: 14px;
                    }
                }
            }
        }

        .stats-cards {
            display: flex;
            gap: 15px;
            flex-wrap: wrap;

            .stats-card {
                flex: 1;
                min-width: 200px;

                .card-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 16px;
                    color: var(--color-text-default);

                    i {
                        color: var(--color-primary);
                    }
                }

                .card-content {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding: 10px 0;

                    .number {
                        font-size: 28px;
                        font-weight: 700;
                        color: var(--color-primary);
                    }

                    .description {
                        font-size: 14px;
                        color: var(--color-text-secondary);
                        margin-top: 5px;
                    }
                }
            }
        }

        .chart-container {
            margin-top: 20px;

            .chart-card {
                margin-bottom: 20px;

                .card-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 16px;
                    color: var(--color-text-default);

                    i {
                        color: var(--color-primary);
                    }
                }

                .chart {
                    height: 350px;
                    width: 100%;
                }
            }
        }

        .data-table-card {
            margin-top: 20px;

            .card-title {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 16px;
                color: var(--color-text-default);

                i {
                    color: var(--color-primary);
                }
            }
        }
    }
}

.status-option {
    display: flex;
    align-items: center;
    gap: 6px;
    
    .status-icon {
        font-size: 16px;
        
        &.scheduled { color: #1890ff; }
        &.running { color: #fa8c16; }
        &.waiting { color: #722ed1; }
        &.done { color: #52c41a; }
        &.failed { color: #f5222d; }
        &.cancelled { color: #bfbfbf; }
    }
}
</style> 